import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DateRangeSelectorComponent} from '../../../layout/date-range-selector/date-range-selector.component';
import {DateRange} from '../../../../types/date-range';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import { DatePipe, NgClass } from '@angular/common';
import {PartnerService} from '../../../../services/partner/partner.service';
import {Partner} from '../../../../models/partner/partner';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {ConfirmService} from '../../../../services/confirm/confirm.service';
import {Paginator} from '../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../tools/table-sorter/table-sorter';
import {LoaderService} from '../../../../services/loader/loader.service';
import {TableSorterDirective} from '../../../../directives/table-sorter/table-sorter.directive';
import {TableSorterHeaderDirective} from '../../../../directives/table-sorter/table-sorter-header.directive';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {forkJoin} from 'rxjs';
import {BillingRequestService} from '../../../../services/accounting/billing-request.service';
import {BillingRequest} from '../../../../models/accounting/billing-request';
import {MetaTitle} from '../../../../decorator/meta-title';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {InvoiceService} from '../../../../services/accounting/invoice.service';
import {PercentPipe} from '../../../../pipes/percent/percent.pipe';
import {ConfirmDirective} from '../../../../directives/confirm/confirm.directive';
import {AuthService} from '../../../../services/auth/auth.service';
import {Config} from '../../../../models/config';
import {TranslatePipe} from '@ngx-translate/core';
import {IsGranted} from '../../../../decorator/is-granted';
import {Invoice} from '../../../../models/accounting/invoice';
import {RouterLink} from '@angular/router';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {AffiliateInvoiceFileComponent} from './affiliate-invoice-file/affiliate-invoice-file.component';

@Component({
  selector: 'app-affiliate-payment',
  imports: [
    ReactiveFormsModule,
    DateRangeSelectorComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    FormsModule,
    DatePipe,
    TableSorterDirective,
    TableSorterHeaderDirective,
    PaginatorComponent,
    MoneyPipe,
    PercentPipe,
    NgClass,
    ConfirmDirective,
    TranslatePipe,
    RouterLink
],
  providers: [DatePipe, TranslatePipe],
  templateUrl: './affiliate-payment.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './affiliate-payment.component.scss'
})
@IsGranted(['AFFILIATE_PAYMENT_VIEW'])
@MetaTitle('Affiliate payment')
export class AffiliatePaymentComponent implements OnInit {

  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('endingAt', 'desc');
  downloadMode: 'csv' | 'zip' = 'zip';

  affiliates: Partner[] = [];
  billingRequests: BillingRequest[] = [];
  selectedBillingRequests: number[] = [];
  selectedInvoices: number[] = [];
  paymentTermList: string[] = [];
  invoiceStatusList: string[] = [];

  filters: FormGroup = new FormGroup({
    startingAt: new FormControl(),
    endingAt: new FormControl(),
    paymentTerms: new FormControl([]),
    status: new FormControl([]),
    partnerId: new FormControl([]),
    deferred: new FormControl(null)
  });

  constructor(
    protected invoiceService: InvoiceService,
    private auth: AuthService,
    private loader: LoaderService,
    private partnerService: PartnerService,
    private billingRequestService: BillingRequestService,
    private confirm: ConfirmService,
    private snackbar: SnackbarService,
    private datePipe: DatePipe,
    private translate: TranslatePipe,
    private dialog: DialogService
  ) {
  }

  ngOnInit(): void {
    this.auth.getAsyncConfig().subscribe((config: Config) => {
      this.paymentTermList = config?.data?.paymentTermList ?? [];
      this.invoiceStatusList = config?.data?.invoiceStatusList ?? [];
    });

    this.partnerService.findAll({accountType: 'affiliate', sort: 'companyName'}).subscribe((affiliates: Partner[]) => {
      this.affiliates = affiliates;

      this.fetchAll();
    });
  }

  isSelected(billingRequest: BillingRequest): boolean {
    return billingRequest.id ? this.selectedBillingRequests.includes(billingRequest.id) : false;
  }

  fetchAll(): void {
    this.loader.startLoading();

    const data: any = this.filters.value;
    const parameters: any = {
      groups: 'file'
    };

    if (data.startingAt) {
      parameters.startingAt = this.datePipe.transform(data.startingAt, 'yyyy-MM-dd');
    }
    if (data.endingAt) {
      parameters.endingAt = this.datePipe.transform(data.endingAt, 'yyyy-MM-dd');
    }
    if (data.status && data.status.length) {
      parameters.status = data.status;
    }
    if (data.partnerId && data.partnerId.length) {
      parameters.partnerId = data.partnerId;
    }
    if (data.deferred !== null) {
      parameters.deferred = data.deferred;
    }

    this.billingRequestService.findAll(parameters, this.paginator, this.tableSorter).subscribe((billingRequests: BillingRequest[]) => {
      this.billingRequests = billingRequests;

      this.loader.stopLoading();
    });
  }

  updateRange(dateRange: DateRange): void {
    this.filters.get('startingAt')?.patchValue(dateRange.start);
    this.filters.get('endingAt')?.patchValue(dateRange.end);
  }

  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedBillingRequests = this.billingRequests
        .map((billingRequest: BillingRequest) => billingRequest.id)
        .filter((id): id is number => id !== null);

      this.selectedInvoices = this.billingRequests
        .map((billingRequest: BillingRequest) => billingRequest.invoice?.id ?? null)
        .filter((id): id is number => id !== null);
    } else {
      this.selectedBillingRequests = [];
      this.selectedInvoices = [];
    }
  }

  selectBillingRequest(billingRequest: BillingRequest): void {
    const invoice: Invoice | null = billingRequest.invoice;

    if (invoice && invoice.id) {
      if (this.selectedInvoices.includes(invoice.id)) {
        const index = this.selectedInvoices.indexOf(invoice.id);
        this.selectedInvoices.splice(index, 1);
      } else {
        this.selectedInvoices.push(invoice.id)
      }
    }

    if (billingRequest && billingRequest.id) {
      if (this.selectedBillingRequests.includes(billingRequest.id)) {
        const index = this.selectedBillingRequests.indexOf(billingRequest.id);
        this.selectedBillingRequests.splice(index, 1);
      } else {
        this.selectedBillingRequests.push(billingRequest.id)
      }
    }
  }

  isAllSelected(): boolean {
    return this.selectedBillingRequests.length === this.billingRequests.length && this.billingRequests.length > 0;
  }

  delete(billingRequest: BillingRequest): void {
    if (billingRequest.id) {
      this.billingRequestService.delete(billingRequest.id).subscribe(() => {
        this.fetchAll();
        this.snackbar.success('Invoice deleted.');
      });
    }
  }

  openInvoiceFile(billingRequest: BillingRequest): void {
    this.dialog.openDialog(AffiliateInvoiceFileComponent, {
      class: 'modal-xl',
      backdrop: false,
      data: {
        billingRequest
      }
    }).subscribe((updated: boolean) => {
      if (updated) {
        this.fetchAll();
      }
    });
  }

  recalculate(billingRequest: BillingRequest): void {
    const invoiceId = billingRequest.invoice?.id;
    if (!invoiceId) {
      return;
    }

    this.confirm.open('Do you really want to recalculate this invoice from the current conversion data?').subscribe((result: boolean) => {
      if (result) {
        this.invoiceService.recalculate(Number(invoiceId)).subscribe(() => {
          this.fetchAll();

          this.snackbar.success('The invoice has been recalculated.');
        });
      }
    });
  }

  recalculateDeferred(billingRequest: BillingRequest): void {
    if (!billingRequest.id) {
      return;
    }

    this.confirm.open('Do you really want to recalculate this deferred billing request from the current conversion data?').subscribe((result: boolean) => {
      if (result) {
        this.billingRequestService.recalculate(Number(billingRequest.id)).subscribe((data: { invoiced: boolean, reference: string | null }) => {
          this.fetchAll();

          if (data.invoiced) {
            this.snackbar.success(`Payment threshold reached: invoice ${data.reference} has been created.`);
          } else {
            this.snackbar.success('The deferred billing request has been recalculated, threshold not reached yet.');
          }
        });
      }
    });
  }

  updateStatus(status: string, billingRequest: BillingRequest | null = null) {
    if (!this.selectedInvoices.length && !billingRequest) {
      return;
    }

    const ids = (billingRequest && billingRequest.invoice) ? [billingRequest.invoice.id] : this.selectedInvoices;
    const count = this.selectedInvoices.length;
    const confirmMessage = (billingRequest || count === 1) ? 'invoice' : `selection of ${count} invoices`;
    const statusLabel = this.translate.transform('invoiceStatus.' + status);

    this.confirm.open(`Do you really want to change the status of this ${confirmMessage} to ${statusLabel}?`).subscribe((result: boolean) => {
      if (result) {
        const requests = [];
        const data: any = {
          status
        };

        for (const id of ids) {
          requests.push(this.invoiceService.update(Number(id), data));
        }

        forkJoin(requests).subscribe(() => {
          this.fetchAll();

          this.snackbar.success(`The status has been successfully changed to ${statusLabel}.`);
        });
      }
    });
  }

}
